import { STORAGE_KEYS } from "@/shared/constants/storage-keys";
import { useThemeStore } from "@/shared/store/theme-store";

import type { ServiceHandler } from "../bridge/message-router";

/**
 * 主题服务：供插件读写宿主外观（主题预设、主色、壁纸与内容区效果、亮暗模式），并与 `theme-store`、CSS 变量及 localStorage 同步。
 *
 * 壁纸与模糊等键使用固定前缀持久化，便于禁用插件或重置时清理；`reset` 与工具函数协同保证宿主回到一致默认态。
 */

/**
 * 移除插件写入的 CSS 变量与「有壁纸」类名，并把主题与颜色模式拉回 store 默认。
 * 不清理壁纸相关的 localStorage，便于插件重新启用时由 `restorePluginBackground` 恢复。
 */
export function clearPluginThemeEffects(): void {
  document.documentElement.style.removeProperty("--plugin-bg-image");
  document.documentElement.style.removeProperty("--plugin-bg-opacity");
  document.documentElement.style.removeProperty("--plugin-content-blur");
  document.documentElement.classList.remove("has-plugin-bg");

  const state = useThemeStore.getState();
  state.actions.setTheme("default");
  state.actions.setColorMode("system");
}

/**
 * 从 localStorage 读取插件持久化的壁纸与模糊等设置，写回 `:root` 样式。
 */
export function restorePluginBackground(): void {
  try {
    const bgImage = localStorage.getItem(STORAGE_KEYS.pluginBgImage);
    const bgOpacity = localStorage.getItem(STORAGE_KEYS.pluginBgOpacity);
    if (bgImage) {
      document.documentElement.style.setProperty("--plugin-bg-image", `url(${bgImage})`);
      document.documentElement.classList.add("has-plugin-bg");
    }
    if (bgOpacity) {
      document.documentElement.style.setProperty("--plugin-bg-opacity", bgOpacity);
    }
    const contentBlur = localStorage.getItem(STORAGE_KEYS.pluginContentBlur);
    if (contentBlur) {
      document.documentElement.style.setProperty("--plugin-content-blur", `${contentBlur}px`);
    }
  } catch {
    // localStorage 不可用时静默忽略
  }
}

const THEME_PRESETS = [
  { name: "默认", value: "default", color: "hsl(240 5.9% 10%)" },
  { name: "红色", value: "red", color: "hsl(0 72.2% 50.6%)" },
  { name: "玫瑰色", value: "rose", color: "hsl(346.8 77.2% 49.8%)" },
  { name: "绿色", value: "green", color: "hsl(142.1 76.2% 36.3%)" },
  { name: "紫色", value: "violet", color: "hsl(262.1 83.3% 57.8%)" },
  { name: "橙色", value: "orange", color: "hsl(24.6 95% 53.1%)" },
  { name: "黄色", value: "yellow", color: "hsl(47.9 95.8% 53.1%)" },
  { name: "蓝色", value: "blue", color: "hsl(221.2 83.2% 53.3%)" },
];

export const themeServiceHandler: ServiceHandler = async (_pluginId, method, params) => {
  const state = useThemeStore.getState();

  switch (method) {
    case "setTheme": {
      /* 切换宿主主题预设（如 default / blue），由 theme-store 驱动全局样式 */
      const { theme } = params as { theme: string };
      state.actions.setTheme(theme as Parameters<typeof state.actions.setTheme>[0]);
      return;
    }

    case "setPrimaryColor": {
      /* 直接覆盖 `--primary`，用于插件自定义强调色（HSL 字符串） */
      const { hsl } = params as { hsl: string };
      document.documentElement.style.setProperty("--primary", hsl);
      return;
    }

    case "setBackgroundImage": {
      /* 设置或清除全页壁纸 URL，同步 `--plugin-bg-image` 与 `has-plugin-bg` 类，并写入持久化键 */
      const { url } = params as { url: string };
      if (url) {
        document.documentElement.style.setProperty("--plugin-bg-image", `url(${url})`);
        document.documentElement.classList.add("has-plugin-bg");
        localStorage.setItem(STORAGE_KEYS.pluginBgImage, url);
      } else {
        document.documentElement.style.removeProperty("--plugin-bg-image");
        document.documentElement.classList.remove("has-plugin-bg");
        localStorage.removeItem(STORAGE_KEYS.pluginBgImage);
      }
      return;
    }

    case "setBackgroundOpacity": {
      /* 调整壁纸层不透明度，与背景图配合使用 */
      const { opacity } = params as { opacity: number };
      document.documentElement.style.setProperty("--plugin-bg-opacity", String(opacity));
      localStorage.setItem(STORAGE_KEYS.pluginBgOpacity, String(opacity));
      return;
    }

    case "setContentBlur": {
      /* 对主内容区施加 backdrop 模糊（像素值），持久化供刷新后恢复 */
      const { blur } = params as { blur: number };
      document.documentElement.style.setProperty("--plugin-content-blur", `${blur}px`);
      localStorage.setItem(STORAGE_KEYS.pluginContentBlur, String(blur));
      return;
    }

    case "setColorMode": {
      /* 亮 / 暗 / 跟随系统，由 theme-store 统一解析并作用到文档 */
      const { mode } = params as { mode: "light" | "dark" | "system" };
      state.actions.setColorMode(mode);
      return;
    }

    case "getCurrentTheme": {
      /* 供插件读取当前主题名、颜色模式偏好、实际解析后的明暗，以及内置预设列表 */
      const isDark = document.documentElement.classList.contains("dark");
      return {
        theme: state.theme,
        colorMode: state.colorMode,
        resolvedMode: isDark ? "dark" : "light",
        presets: THEME_PRESETS,
      };
    }

    case "reset": {
      /* 清除插件相关的 CSS 变量与本地存储键，并将主题与颜色模式恢复为默认值 */
      document.documentElement.style.removeProperty("--primary");
      document.documentElement.style.removeProperty("--plugin-bg-image");
      document.documentElement.style.removeProperty("--plugin-bg-opacity");
      document.documentElement.style.removeProperty("--plugin-content-blur");
      document.documentElement.classList.remove("has-plugin-bg");
      localStorage.removeItem(STORAGE_KEYS.pluginBgImage);
      localStorage.removeItem(STORAGE_KEYS.pluginBgOpacity);
      localStorage.removeItem(STORAGE_KEYS.pluginContentBlur);
      state.actions.setTheme("default");
      state.actions.setColorMode("system");
      return;
    }

    default:
      throw Object.assign(new Error(`未知 theme 方法: ${method}`), { code: "UNKNOWN_METHOD" });
  }
};
